import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Pill,
  RefreshCw,
  Trash2,
  Plus,
  UserPlus,
  Search,
  Loader2,
  Pencil,
  Building,
  Link2,
  LogIn,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  nome: "",
  email: "",
  password: "",
  active: true,
  organization_ids: [],
  via: "",
  civico: "",
  cap: "",
  comune: "",
  provincia: "",
};

export default function FarmacieAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [farmacie, setFarmacie] = useState([]);
  const [struttureOptions, setStruttureOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [struttureSearch, setStruttureSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [listRes, optRes] = await Promise.all([
        fetch(`${API}/api/admin/farmacie`, { headers }),
        fetch(`${API}/api/admin/farmacie/strutture-options`, { headers }),
      ]);
      if (!listRes.ok) {
        const j = await listRes.json().catch(() => ({}));
        throw new Error(j.detail || "Errore caricamento farmacie");
      }
      const listJson = await listRes.json();
      setFarmacie(listJson.data || []);
      if (optRes.ok) {
        const optJson = await optRes.json();
        setStruttureOptions(optJson.data || []);
      }
    } catch (e) {
      toast.error(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setStruttureSearch("");
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditId(f.id);
    setForm({
      nome: f.nome || "",
      email: f.email || "",
      password: "",
      active: f.active !== false,
      organization_ids: [...(f.organization_ids || [])],
      via: f.via || "",
      civico: f.civico || "",
      cap: f.cap || "",
      comune: f.comune || "",
      provincia: f.provincia || "",
    });
    setStruttureSearch("");
    setDialogOpen(true);
  };

  const toggleOrg = (orgId) => {
    setForm((prev) => {
      const set = new Set(prev.organization_ids);
      if (set.has(orgId)) set.delete(orgId);
      else set.add(orgId);
      return { ...prev, organization_ids: Array.from(set) };
    });
  };

  const save = async () => {
    if (!form.nome.trim()) {
      toast.error("Inserisci il nome della farmacia");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Inserisci l'email");
      return;
    }
    if (!editId && !form.password) {
      toast.error("Inserisci una password");
      return;
    }
    if (form.password && form.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        const payload = {
          nome: form.nome.trim(),
          email: form.email.trim(),
          active: form.active,
          via: form.via.trim(),
          civico: form.civico.trim(),
          cap: form.cap.trim(),
          comune: form.comune.trim(),
          provincia: form.provincia.trim(),
        };
        if (form.password) payload.password = form.password;

        const res = await fetch(`${API}/api/admin/farmacie/${editId}`, {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Aggiornamento fallito");

        const strRes = await fetch(`${API}/api/admin/farmacie/${editId}/strutture`, {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ organization_ids: form.organization_ids }),
        });
        const strJson = await strRes.json().catch(() => ({}));
        if (!strRes.ok) throw new Error(strJson.detail || "Collegamento strutture fallito");

        toast.success("Farmacia aggiornata");
      } else {
        const res = await fetch(`${API}/api/admin/farmacie`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            nome: form.nome.trim(),
            email: form.email.trim(),
            password: form.password,
            active: form.active,
            organization_ids: form.organization_ids,
            via: form.via.trim(),
            civico: form.civico.trim(),
            cap: form.cap.trim(),
            comune: form.comune.trim(),
            provincia: form.provincia.trim(),
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.detail || "Creazione fallita");
        toast.success("Farmacia creata");
      }
      setDialogOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (f, active) => {
    try {
      const res = await fetch(`${API}/api/admin/farmacie/${f.id}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.detail || "Aggiornamento fallito");
      }
      setFarmacie((prev) => prev.map((x) => (x.id === f.id ? { ...x, active } : x)));
      toast.success(active ? "Farmacia attivata" : "Farmacia disattivata");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const impersonate = async (f) => {
    if (!f?.id) return;
    setImpersonatingId(f.id);
    try {
      const res = await fetch(`${API}/api/admin/farmacie/${f.id}/impersonate`, {
        method: "POST",
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore impersonazione");
      if (!json.token) throw new Error("Token non ricevuto");
      const url = new URL(window.location.origin);
      url.pathname = "/farmacia";
      url.searchParams.set("farmacia_impersonate_token", json.token);
      window.open(url.toString(), "_blank");
      toast.success(`Portale Farmacia aperto come ${f.nome || f.email}`);
    } catch (e) {
      toast.error(e.message || "Errore durante l'accesso");
    } finally {
      setImpersonatingId(null);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Eliminare la farmacia "${f.nome}"? L'accesso al portale verrà revocato.`)) {
      return;
    }
    setDeletingId(f.id);
    try {
      const res = await fetch(`${API}/api/admin/farmacie/${f.id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.detail || "Eliminazione fallita");
      }
      toast.success("Farmacia eliminata");
      await loadAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = farmacie.filter((f) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (f.nome || "").toLowerCase().includes(q) ||
      (f.email || "").toLowerCase().includes(q)
    );
  });

  const filteredStrutture = struttureOptions.filter((s) => {
    const q = struttureSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.name || "").toLowerCase().includes(q) ||
      (s.struttura_nome || "").toLowerCase().includes(q) ||
      (s.account_nome || "").toLowerCase().includes(q) ||
      (s.comune || "").toLowerCase().includes(q) ||
      (s.organizationId || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Pill className="h-5 w-5 text-emerald-600" />
            Farmacia
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Crea e gestisci gli account del portale Farmacia (
            <code className="text-xs bg-slate-100 px-1 rounded">/farmacia-login</code>
            ). Collega le strutture Affidya che ogni farmacia potrà gestire.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button size="sm" onClick={openCreate}>
            <UserPlus className="h-4 w-4 mr-1" />
            Nuova farmacia
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Cerca per nome o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && farmacie.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Caricamento…
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((f) => (
            <Card key={f.id}>
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{f.nome}</p>
                    {f.active === false ? (
                      <Badge variant="secondary" className="text-xs">Disattivata</Badge>
                    ) : (
                      <Badge className="text-xs bg-emerald-600">Attiva</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{f.email}</p>
                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-1 flex-wrap">
                    <Building className="w-3 h-3" />
                    {f.strutture_count === 0
                      ? "Nessuna struttura collegata"
                      : (
                        <>
                          {(f.strutture || []).length > 0
                            ? (f.strutture || []).map((s) => s.nome).filter(Boolean).join(", ")
                            : f.strutture_count === 1
                              ? "1 struttura collegata"
                              : `${f.strutture_count} strutture collegate`}
                        </>
                      )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={f.active !== false}
                      onCheckedChange={(v) => toggleActive(f, v)}
                    />
                    <span className="text-xs text-slate-500 hidden sm:inline">Attiva</span>
                  </div>
                  {f.active !== false && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={impersonatingId === f.id}
                      onClick={() => impersonate(f)}
                      className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                      title="Apri il portale Farmacia come questo account"
                    >
                      {impersonatingId === f.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <LogIn className="w-4 h-4 mr-1" />
                      )}
                      Login
                      <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(f)}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600"
                    disabled={deletingId === f.id}
                    onClick={() => remove(f)}
                  >
                    {deletingId === f.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                {search.trim()
                  ? "Nessuna farmacia corrisponde alla ricerca."
                  : "Nessuna farmacia. Creane una per abilitare l'accesso a /farmacia-login."}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editId ? (
                <>
                  <Pencil className="w-4 h-4" />
                  Modifica farmacia
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Nuova farmacia
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div>
              <Label>Nome farmacia</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                placeholder="es. Farmacia Centrale"
              />
            </div>
            <div>
              <Label>Email di accesso</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="farmacia@esempio.it"
              />
            </div>
            <div>
              <Label>{editId ? "Nuova password (opzionale)" : "Password"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={editId ? "Lascia vuoto per non cambiare" : "Minimo 6 caratteri"}
              />
            </div>
            <div className="border-t pt-4 space-y-3">
              <Label className="text-base">Sede (consegne)</Label>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Via</Label>
                  <Input
                    value={form.via}
                    onChange={(e) => setForm((f) => ({ ...f, via: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Civico</Label>
                  <Input
                    value={form.civico}
                    onChange={(e) => setForm((f) => ({ ...f, civico: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">CAP</Label>
                  <Input
                    value={form.cap}
                    onChange={(e) => setForm((f) => ({ ...f, cap: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Comune</Label>
                  <Input
                    value={form.comune}
                    onChange={(e) => setForm((f) => ({ ...f, comune: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">Prov.</Label>
                  <Input
                    maxLength={2}
                    value={form.provincia}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, provincia: e.target.value.toUpperCase() }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              />
              <Label>Account attivo</Label>
            </div>

            <div className="border-t pt-4 space-y-3">
              <Label className="flex items-center gap-2 text-base">
                <Link2 className="w-4 h-4 text-emerald-600" />
                Strutture collegate ({form.organization_ids.length})
              </Label>
              <p className="text-xs text-slate-500">
                Seleziona le singole strutture Affidya (anche secondarie dello stesso account).
                La farmacia vedrà solo i dati delle strutture con collegamento attivo.
                Ogni struttura può avere una sola farmacia attiva: al salvataggio i collegamenti
                rimossi restano in storico e un eventuale collegamento attivo di un&apos;altra
                farmacia sulla stessa struttura viene cessato.
              </p>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Filtra per struttura o account..."
                  value={struttureSearch}
                  onChange={(e) => setStruttureSearch(e.target.value)}
                />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1 border rounded-lg p-2">
                {filteredStrutture.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Nessuna struttura</p>
                ) : (
                  filteredStrutture.map((s) => {
                    const checked = form.organization_ids.includes(s.organizationId);
                    return (
                      <label
                        key={s.organizationId}
                        className="flex items-start gap-2 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleOrg(s.organizationId)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                            <span className="truncate">{s.struttura_nome || s.name}</span>
                            {s.is_primary ? (
                              <Badge variant="secondary" className="text-[10px] shrink-0">Principale</Badge>
                            ) : null}
                          </span>
                          {(s.account_nome || s.comune) && (
                            <span className="block text-xs text-slate-400 truncate">
                              {[s.account_nome, s.comune].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editId ? "Salva" : "Crea farmacia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
